import { flow, makeAutoObservable } from 'mobx';
import { loadFromStorage, loadFromStorageJSON, saveToStorage, saveToStorageJSON } from '@desktopUtils/storage';
import { getCustomizationFields } from '../utils/workspaceCustomization';
import { post, get } from '../utils/queryPortal';
import { getDateCreate, setServerTimeZone } from '../utils/format';
import { executeCustomJSFromConfigList } from '@desktopUtils/executeCustomJSFromConfigList';
import { statsAddEvent, statsAddError, startTrackTime } from '../utils/portalStats';
import { viewJurisdiction } from '../map/mapToolbar';
import { ROUTES, NEW_ROUTES } from '../constants';
import { getPathname } from '../utils';
import { auth } from '../constants/firebase';

export function getProfileAndPortal(loc) {
  // get profile from url
  // use it only on a first load, then use app.profileID
  // because url gets broken all the time
  const profileStart = loc.indexOf('/#/') + 3;
  const profileEnd = loc.indexOf('/', profileStart) > 0 ? loc.indexOf('/', profileStart) : loc.length;
  let profile = loc.substring(profileStart, profileEnd);
  const id = profile.lastIndexOf('-') > -1 ? profile.substr(profile.lastIndexOf('-') + 1) : null;
  if (profile.lastIndexOf('-') > -1) profile = profile.substr(0, profile.lastIndexOf('-'));
  return [profile, id];
}

export function statsAddErrorLimited(pars) {

}

const setDbConfig = (dbConfig) => {
  if (dbConfig) {
    let dbConfigProc = executeCustomJSFromConfigList(dbConfig);
    // put configs
    let cfg = {};
    dbConfigProc.forEach(d => { cfg[d.Name] = d.Value; });
    // trim end slash 'https://dev2.maintstar.co/' => 'https://dev2.maintstar.co'
    cfg.BASE_URL = cfg.BASE_URL?.replace(/\/$/g, '');
    // as soon as possible
    viewJurisdiction(window.map);
    return cfg;
  }

  return dbConfig;
}

class App {
  isPortal = true;

  loginVisible = false;
  isMenuClosed = false;
  isMapOpenTouched = false;
  // @observable isRightOpen = window.matchMedia("(max-width: 505px)").matches ? false : JSON.parse(loadFromStorage('isRightOpen') || 'true');
  isRightOpen = !window.matchMedia('(max-width: 505px)').matches;
  isAppLoading = false;
  profilesLoaded = false;
  error = null;

  // loading, need to change to counter, instead of true, false, otherwise
  // will work wrong with many queries.
  isLoading = 0;
  token;
  anonymousSessionExpired = false;
  portalUnavailable = false;

  profile = '';
  profiles = [];
  portal_id_prefix = 'default-1';
  portalID = 1;
  portalSettings = {};
  captchaEnabled = false;
  captchaSiteKey = '';
  invalidConfig = false;

  userID = null;
  tokenOK = true;
  userPosition = {};
  settings = {};
  showLanguagesLicensed = false;
  dbConfig = {};
  customizations = [];
  doubleClickOptions = [];
  userInfo = {};
  wrGroupInfo = {};
  groupedIssues = {};
  // errorEventCounters = {};

  wrOperations = [];
  wrIssues = [];
  wrConcerns = [];
  wrFrequentlyUsedIssues = [];
  showLogin = false;
  ssoLogonError = null;
  links = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });

    this.initialLoad();
  }

  initialLoad = flow(function* () {
    yield this.init();
    if (this.getToken()) {
      yield this.loadOperations();
    }
  })

  // stub session_refreshToken for desktop query
  session_refreshToken = () => {

  }

  loadLinks = flow(function* () {
    const urls = yield post('DQ_wr_urls.php', {});

    this.links = urls.value;
  })

  loadUserDetails = flow(function* () {
    const { wrSetup, dbConfig, userInfo, userID, wrGroupInfo } = yield post('WR_AppUser.php', { portalID: this.portalID });
    this.userID = userID;
    this.userInfo = userInfo;
    this.wrGroupInfo = wrGroupInfo;

    if (dbConfig) {
      this.dbConfig = setDbConfig(dbConfig);
      this.settings.showLanguages = this.showLanguagesLicensed && Boolean(this.dbConfig?.LANGUAGE_OPTIONS);
    }
    this.settings.skipPreviewPage = wrSetup?.skip_preview_page === 'Y';

    return ({ wrSetup, dbConfig, userInfo, userID, wrGroupInfo });
  });

  isAuthorized = () => {
    const token = this.getToken();
    return token && token !== 'null' && this.tokenOK;
  }

  isNewDesign = () => {
    return this.isUserNewDesign() || this.isPortalSettingsNewDesign();
  }

  isUserNewDesign = () => {
    return this.userInfo?.is_new_design === 'Y'
  }

  isPortalSettingsNewDesign = () => {
    return this.portalSettings?.is_new_design === 'Y'
  }

  init = flow(function* () {
    // todo: check reload
    const reload = this.checkInactiveReload();
    if (reload) return;

    const [hashPath, hashQuery] = window.location.hash.split('?');
    const ssoQuery = new URLSearchParams(hashQuery || '');
    if (ssoQuery.get('ssoSkey')) {
      this.setToken(ssoQuery.get('ssoSkey'), ssoQuery.get('ssoRefresh'), ssoQuery.get('ssoExpires'));
      this.setUserId(ssoQuery.get('ssoUser'));
    }
    if (ssoQuery.get('ssoLogonError')) {
      this.ssoLogonError = ssoQuery.get('ssoLogonError');
      this.loginVisible = true;
    }
    if (ssoQuery.get('ssoSkey') || ssoQuery.get('ssoLogonError')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + hashPath);
    }

    const { data: profiles } = yield get('Profiles.php', {}, { isGlobal: true, skipProfile: true, skipIndicator: true });
    this.profiles = profiles;
    if (!profiles?.length) {
      this.profile = null;
      this.portalID = null;
      return;
    }
    let [profile, portalID] = getProfileAndPortal(window.location.href);
    this.profile = profiles.includes(profile) ? profile : (profiles.includes('default') ? 'default' : profiles[0]);
    this.portalID = Number(portalID) || 1;

    const { settings, portal, showLanguages, captchaSiteKey } = yield post('WR_SysSettings.php', { portal_id: this.portalID });
    this.showLanguagesLicensed = ['1', 1, 'Y'].includes(showLanguages);
    this.captchaSiteKey = captchaSiteKey || '';
    if (!portal?.portal_id) {
      this.portalID = null;
      this.portalSettings = {};
      this.invalidConfig = true;
      return;
    }
    this.portalID = portal.portal_id;
    this.portalSettings = portal;

    this.captchaEnabled = [1, '1'].includes(this.portalSettings?.captcha_enabled);
    this.settings = settings;
    this.settings.showMotorPool = ['1', 1, 'Y'].includes(this.portalSettings?.show_motorpool);
    this.settings.showGoogleLogin = Boolean(auth) && this.portalSettings?.sso_allow_flag === 'Y';
    this.settings.showFacebookLogin = Boolean(auth) && this.portalSettings?.sso_allow_facebook_flag === 'Y';
    this.settings.SSO = this.portalSettings?.sso_flag === 'Y';
    this.settings.showLanguages = false;

    // load login customization
    this.loadCustomizations();

    // check user session
    const token = loadFromStorage('token_p');
    const expires = loadFromStorage('tokenExpires_p');
    const refreshToken = loadFromStorage('refreshToken_p');
    const storedUserID = loadFromStorage('userID');
    const hasStoredSession = token && expires && refreshToken;
    const allowAnonymous = this.portalSettings?.allow_anonymous === 'Y';
    const allowBackgroundAnonymous = this.isPortalSettingsNewDesign() && allowAnonymous;
    const isStoredAnonymous = storedUserID?.toLowerCase() === 'anonymous';
    let userID;

    if (hasStoredSession && (!isStoredAnonymous || allowAnonymous)) {
      this.setUserId(storedUserID);
      this.setAppLoading(true);
      try {
        ({ userID } = yield this.loadUserDetails());
      } catch (error) {
        userID = null;
      }
    } else {
      // prevent cache errors
      this.setToken(null);
      this.setUserId(null);
    }

    if (this.anonymousSessionExpired) {
      return;
    }

    if (!userID && allowBackgroundAnonymous) {
      this.setToken(null);
      this.setUserId(null);
      this.setAppLoading(true);
      try {
        const login = yield post('WR_Login.php', {
          userid: 'Anonymous',
          passwd: '',
          portal_id: this.portalID,
        }, {
          skipError: true,
          skipLogging: true,
        });

        if (login?.rc === 0 && login?.session_id) {
          yield this.session_login(login, true);
          userID = this.userID;
        }
      } catch (error) {
        userID = null;
      }
    }

    if (!userID) {
      this.setToken(null);
      this.setUserId(null);
      return;
    }
    setServerTimeZone(this.settings.timezone);

    if (this.portalSettings.show_map === 'N') {
      this.hideRight();
    }

    window.navigator.geolocation.getCurrentPosition((pos) => {
      this.userPosition = pos;
    }, (error) => {});
    window.addEventListener('online', this.updateStatus);
    window.addEventListener('offline', this.updateStatus);

    // todo: review stats
    startTrackTime();

    this.setAppLoading(false);
  });

  loadOperations = async () => {
    const operations = await post('WR_Operation-List.php', {});
    const issues = await post('DQ_wr_issue.php', { });
    const concerns = await post('DQ_wr_concern.php', { });
    const wrFrequentlyUsedIssues = await post('WR_FrequentlyUsedOperations.php', {});
    const allowedOpIds = new Set((operations.value || []).map((operation) => `${operation.op_id}`));
    const filteredIssues = (issues.value || []).filter((issue) => allowedOpIds.has(`${issue.op_id}`));
    const filteredFrequentlyUsedIssues = (wrFrequentlyUsedIssues.value || []).filter((issue) => allowedOpIds.has(`${issue.op_id}`));

    this.wrOperations = operations.value;
    this.wrIssues = filteredIssues;
    this.wrConcerns = concerns.value;
    this.wrFrequentlyUsedIssues = filteredFrequentlyUsedIssues;
    this.groupedIssues = filteredIssues.reduce((acc, issue) => {
      if (!acc[issue.op_id] ) {
        acc[issue.op_id] = [];
      }
      acc[issue.op_id] = [...acc[issue.op_id], issue]

      return acc;
    }, {});
  }

  loadCustomizations = flow(function* () {
    const res = yield post('WR_Customizations.php', { portal_id: this.portalID });
    const custNames = [
      'wr_portal',
      'wr_portal_logon',
      'wr_portal_reg',
      'wr_portal_user',
      'wr_portal_reservation',
      'wr_portal_reservation_details',
      'wr_portal_submitted',
      'wr_portal_operation',
      'portalRequests',
      'portalReservation',
    ];
    const customizations = {};
    custNames.forEach(d => {
      customizations[d] = getCustomizationFields(res[d] || []);
    });
    this.customizations = customizations;
    this.doubleClickOptions = res.doubleClick || [];
  });

  getToken = () => {
    return loadFromStorage('token_p') || this.token || null;
  }

  setToken = (token, refreshToken = '', tokenExpiresTime = '') => {
    this.tokenOK = !!token;
    this.token = token;
    this.isAppLoading = false;
    if (token) {
      this.anonymousSessionExpired = false;
    }
    saveToStorage('token_p', token);
    saveToStorage('refreshToken_p', refreshToken);
    saveToStorage('tokenExpires_p', tokenExpiresTime);
    saveToStorage('tokenReloading_p', '');
  };

  invalidateSession = () => {
    this.anonymousSessionExpired = true;
    this.setToken(null);
    this.setUserId(null);
  };

  setPortalUnavailable = () => {
    this.portalUnavailable = true;
  };

  session_login = async (res, noReload = false) => {
    // always refresh page after login, it will update client version
    // const versionUpdateKeyCurrent = loadFromStorage('versionUpdateKey');
    // console.log('version login:', versionUpdateKeyCurrent, res.versionUpdateKey, this.versionUpdateKey);
    // this.changeVersionKey(res.versionUpdateKey); // not needed, App-List.php already updates version key
    // this.saveVersionKey();
    this.setToken(res.session_id, res.refresh_token, res.expiration_dt);
    if (res.userid) {
      this.setUserId(res.userid);
    }
    if (!noReload) {
      location.reload();
    }
  }

  session_getMinutesToAction = () => {
    return false;
  }
  updateStatus = () => {
    console.log('online:', navigator.onLine);
  }

  // todo: review below
  setUserId(userid) {
    this.userID = userid || '';
    saveToStorage('userID', this.userID);
  }

  setAppLoading = (loading) => {
    this.isAppLoading = loading;
  };

  setLoading = (loading) => {
    if (loading < 0 && this.isLoading === 0) {
      return;
    }
    this.isLoading = loading;
  };

  setLoginVisible = (isOpen) => {
    this.loginVisible = isOpen;
  }

  mapToggle = () => {
    this.isRightOpen = !this.isRightOpen;
    saveToStorage('isRightOpen', this.isRightOpen);
    this.isMapOpenTouched = true;
  };
  rightToggle = () => {
    this.isRightOpen = !this.isRightOpen;
    saveToStorage('isRightOpen', this.isRightOpen);
    this.invalidateSize();
  };

  hideRight = () => {
    this.isRightOpen = false;
    saveToStorage('isRightOpen', this.isRightOpen);
  };

  showRight = () => {
    this.isRightOpen = true;
    saveToStorage('isRightOpen', this.isRightOpen);
    this.invalidateSize();
  };

  invalidateSize = () => {
    setTimeout(() => {
      const map = window.map || window?.mapStore?.map;
      map?.invalidateSize?.();
    }, 100);
  };

  checkInactiveReload = () => {
    // if open for more than 8 hours, reload
    // localStorage.removeItem('portalLastTimerTimestamp')
    const lastTimeCheck = Number(loadFromStorage('portalLastTimerTimestamp') || 0);
    const currentTimeCheck = (new Date()).getTime();
    const timeDiffHrs = (currentTimeCheck - lastTimeCheck) / 1000 / 3600;
    if (timeDiffHrs > 2) {
      saveToStorage('portalLastTimerTimestamp', (new Date()).getTime());
      location.reload();
      return true;
    }
    return false;
  }

  statsAddEvent = statsAddEvent;
  statsAddError = statsAddError;

  isMobile = () => window.innerWidth < 840

  isAnonymous = () => this.userID?.toLowerCase() === 'anonymous' || !this.userID

  addEventLog = (ev) => {
    let recentEvents = loadFromStorageJSON('recentEvents_portal');
    if (!recentEvents?.length) recentEvents = [];

    recentEvents.unshift({ ...ev, date: getDateCreate('now') });
    recentEvents = recentEvents.slice(0, 30);
    saveToStorageJSON('recentEvents_portal', recentEvents);
  }

  statsAddErrorLimited = statsAddErrorLimited;
}

export const app = new App();

window.navigator.geolocation.watchPosition(
  // on change position
  (props) => {
    app.userPosition = props.coords;
  },
  // error
  (e) => {
    console.log('geoLocationUpdate error', e);
  },
  // watch options
  {
    timeout: 30000,
    maximumAge: 60000,
  },
);
